import { useEffect } from "react";
import { useQuery } from "react-query";
import { useForm } from "react-hook-form";
import Select, { VirtualisedMenuList } from "src/components/Select";
import Radio from "src/components/Radio";
import Textarea from "src/components/Textarea";
import {
  getSchools,
  getQualification,
  getQualifications,
} from "src/models/fields";
import { setDefaultValues } from "src/utils";

const defaultValues = {
  StillAtLastSchool: "",
  SchoolID: "",
  ResultsGCSEByEndOfYear11: "",
  QualsGCSEByEndOfYear11: "",
  AchievedEnglishGCSEByEndOfYear11: "",
  AchievedMathsGCSEByEndOfYear11: "",
  ListGCSEByEndOfYear11: "",
};

const EducationHistory = ({ data, onSubmit }) => {
  // Data loading
  const schools = useQuery("schools", getSchools);
  const qualification = useQuery("qualification", getQualification);
  const qualifications = useQuery("qualifications", getQualifications);

  // Form
  const { control, register, handleSubmit, formState, watch, trigger } =
    useForm({
      mode: "all",
      defaultValues: setDefaultValues(defaultValues, data),
    });
  const { errors, isSubmitting, touchedFields } = formState;

  const stillAtLastSchool = watch("StillAtLastSchool");

  // Validate SchoolID field
  useEffect(() => {
    if ("SchoolID" in touchedFields) trigger("SchoolID");
  }, [stillAtLastSchool]);

  return (
    <>
      <h2>Education History</h2>

      <p>
        This section must be completed in order for your application to be
        processed. If you have not yet taken your exams, please provide us with
        your predicted grades below and bring with you a signed copy of these
        from your school when you attend your interview. If these are not yet
        available please let us know what you are studying below and bring the
        signed copy of them with you to your interview. Please include
        information on all qualifications, ie GCSE, NVQs, BTEC, Diplomas, A
        levels, or any other subjects you may have studied.
      </p>

      <p>
        Please enter the details of your prior qualifications, choosing from the
        drop-down lists below.
      </p>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-12 col-md-6">
            <Radio
              label="Are you still at school or finished school in the last 12 months?"
              name="StillAtLastSchool"
              options={[
                { label: "Yes", value: "1" },
                { label: "No", value: "0" },
              ]}
              inline
              register={register}
              validation={{ required: "This field is required" }}
              error={errors.StillAtLastSchool}
            />
          </div>

          <div className="col-12 col-md-6">
            <Select
              label="Most Recent School"
              name="SchoolID"
              control={control}
              isLoading={schools.isLoading}
              options={schools.data}
              placeholder="Start Typing..."
              validation={{
                required: stillAtLastSchool === "1" && "This field is required",
              }}
              error={errors.SchoolID}
              components={{ MenuList: VirtualisedMenuList }}
              maxMenuHeight={320}
            />
          </div>

          <div className="col-12 col-md-6">
            <Select
              label="Are these results predicted or achieved results?"
              name="ResultsGCSEByEndOfYear11"
              options={[
                { value: "predicted", label: "Predicted" },
                { value: "achieved", label: "Achieved" },
                { value: "none", label: "None of the above" },
              ]}
              control={control}
              validation={{ required: "This field is required" }}
              error={errors.ResultsGCSEByEndOfYear11}
            />
          </div>

          <div className="col-12 col-md-6">
            <Select
              label="Qualification Details"
              name="QualsGCSEByEndOfYear11"
              control={control}
              isLoading={qualifications.isLoading}
              options={qualifications.data}
              validation={{ required: "This field is required" }}
              error={errors.QualsGCSEByEndOfYear11}
            />
          </div>

          <div className="col-12 col-md-6">
            <Select
              label="English Qualification"
              name="AchievedEnglishGCSEByEndOfYear11"
              control={control}
              isLoading={qualification.isLoading}
              options={qualification.data}
              validation={{ required: "This field is required" }}
              error={errors.AchievedEnglishGCSEByEndOfYear11}
            />
          </div>

          <div className="col-12 col-md-6">
            <Select
              label="Math Qualification"
              name="AchievedMathsGCSEByEndOfYear11"
              control={control}
              isLoading={qualification.isLoading}
              options={qualification.data}
              validation={{ required: "This field is required" }}
              error={errors.AchievedMathsGCSEByEndOfYear11}
            />
          </div>

          <div className="col-12">
            <Textarea
              label="Please list out your exam subjects results below (e.g Art - B):"
              name="ListGCSEByEndOfYear11"
              register={register}
            />
          </div>
        </div>

        <button type="submit" className="form-submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving" : "Next"}
        </button>
      </form>
    </>
  );
};

export default EducationHistory;
